import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  MessageCircle, 
  User, 
  Users, 
  Plus,
  Search,
  AlertCircle,
  CheckCircle,
  X,
  Shield,
  UserCheck,
  UserX
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const MessengerPermissionsSettings = () => {
  const [users, setUsers] = useState([]);
  const [messengerPermissions, setMessengerPermissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userPermissions, setUserPermissions] = useState({
    can_message_users: [],
    can_be_messaged_by: [],
    is_restricted: true
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/search`);
      setUsers(response.data);
      setAvailableUsers(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    }
  };

  const fetchAllMessengerPermissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/messenger-permissions`);
      setMessengerPermissions(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch messenger permissions",
        variant: "destructive",
      });
    }
  };

  const fetchUserPermissions = async (userId) => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/messenger-permissions/${userId}`);
      setUserPermissions(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch user permissions",
        variant: "destructive",
      });
      setUserPermissions({
        can_message_users: [],
        can_be_messaged_by: [],
        is_restricted: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAllMessengerPermissions();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchUserPermissions(selectedUser);
    }
  }, [selectedUser]);

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    try {
      await axios.post(`${API}/admin/messenger-permissions/${selectedUser}`, userPermissions);
      
      fetchAllMessengerPermissions();
      
      toast({
        title: "Success",
        description: "Messenger permissions updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update messenger permissions",
        variant: "destructive",
      });
    }
  };

  const handleAddAllowedUser = (userId, type) => {
    setUserPermissions(prev => ({
      ...prev,
      [type]: [...prev[type], userId]
    }));
  };

  const handleRemoveAllowedUser = (userId, type) => {
    setUserPermissions(prev => ({
      ...prev,
      [type]: prev[type].filter(id => id !== userId)
    }));
  };

  const handleToggleRestriction = () => {
    setUserPermissions(prev => ({
      ...prev,
      is_restricted: !prev.is_restricted,
      // Clear allowed users if removing restrictions
      can_message_users: !prev.is_restricted ? [] : prev.can_message_users,
      can_be_messaged_by: !prev.is_restricted ? [] : prev.can_be_messaged_by
    }));
  };

  const getAvailableUsersForType = (type) => {
    return availableUsers.filter(user => 
      user.id !== selectedUser && 
      !userPermissions[type].includes(user.id)
    );
  };

  const selectedUserData = users.find(u => u.id === selectedUser);
  const filteredUsers = users.filter(user =>
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-slate-900">Messenger Permissions</h3>
        </div>
        <p className="text-sm text-slate-600">
          Control who can message whom in the system. Set restrictions and allowed contacts for each user.
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* User Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Select User to Manage</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mb-2"
              />
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user to manage messaging permissions" />
                </SelectTrigger>
                <SelectContent>
                  {filteredUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{user.full_name} (@{user.username})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {selectedUser && selectedUserData && (
          <>
            {/* Selected User Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-5 h-5 text-slate-600" />
                <h4 className="font-medium text-slate-900">{selectedUserData.full_name}</h4>
                <Badge variant="outline">@{selectedUserData.username}</Badge>
              </div>
              <p className="text-sm text-slate-600">{selectedUserData.email}</p>
            </div>

            {/* Restriction Toggle */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-slate-600" />
                  <div>
                    <div className="font-medium text-slate-900">Message Restrictions</div>
                    <div className="text-sm text-slate-500">
                      {userPermissions.is_restricted 
                        ? 'User can only message approved contacts' 
                        : 'User can message anyone in the system'
                      }
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={userPermissions.is_restricted 
                    ? 'bg-red-100 text-red-800 border-red-200' 
                    : 'bg-green-100 text-green-800 border-green-200'
                  }>
                    {userPermissions.is_restricted ? 'Restricted' : 'Unrestricted'}
                  </Badge>
                  <Button
                    onClick={handleToggleRestriction}
                    variant="outline"
                    size="sm"
                  >
                    {userPermissions.is_restricted ? 'Remove Restrictions' : 'Add Restrictions'}
                  </Button>
                </div>
              </div>

              {/* Allowed Contacts - Only show if restricted */}
              {userPermissions.is_restricted && (
                <div className="space-y-4">
                  {/* Users they can message */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-green-600" />
                        <Label className="text-sm font-medium">Can Message These Users</Label>
                        <Badge variant="outline" className="text-xs">
                          {userPermissions.can_message_users.length} allowed
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {/* Add new allowed user */}
                      <div className="flex gap-2">
                        <Select onValueChange={(userId) => handleAddAllowedUser(userId, 'can_message_users')}>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Add user they can message" />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailableUsersForType('can_message_users').map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.full_name} (@{user.username})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Current allowed users */}
                      <div className="space-y-1">
                        {userPermissions.can_message_users.map((userId) => {
                          const user = users.find(u => u.id === userId);
                          return user ? (
                            <div key={userId} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-green-600" />
                                <span className="text-sm">{user.full_name} (@{user.username})</span>
                              </div>
                              <Button
                                onClick={() => handleRemoveAllowedUser(userId, 'can_message_users')}
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-600"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : null;
                        })}
                        {userPermissions.can_message_users.length === 0 && (
                          <div className="text-sm text-slate-500 text-center py-4">
                            No users selected. User cannot initiate conversations.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Users who can message them */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <UserX className="w-4 h-4 text-blue-600" />
                        <Label className="text-sm font-medium">Can Be Messaged By These Users</Label>
                        <Badge variant="outline" className="text-xs">
                          {userPermissions.can_be_messaged_by.length} allowed
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {/* Add new allowed user */}
                      <div className="flex gap-2">
                        <Select onValueChange={(userId) => handleAddAllowedUser(userId, 'can_be_messaged_by')}>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Add user who can message them" />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailableUsersForType('can_be_messaged_by').map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.full_name} (@{user.username})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Current allowed users */}
                      <div className="space-y-1">
                        {userPermissions.can_be_messaged_by.map((userId) => {
                          const user = users.find(u => u.id === userId);
                          return user ? (
                            <div key={userId} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-blue-600" />
                                <span className="text-sm">{user.full_name} (@{user.username})</span>
                              </div>
                              <Button
                                onClick={() => handleRemoveAllowedUser(userId, 'can_be_messaged_by')}
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-600"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : null;
                        })}
                        {userPermissions.can_be_messaged_by.length === 0 && (
                          <div className="text-sm text-slate-500 text-center py-4">
                            No users selected. User cannot receive messages from anyone.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end">
                <Button onClick={handleSavePermissions}>
                  Save Permissions
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Current System Overview */}
        <div className="space-y-4">
          <h4 className="font-medium text-slate-900 flex items-center gap-2">
            <Users className="w-4 h-4" />
            System Overview
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-800">
                {messengerPermissions.filter(p => !p.is_restricted).length}
              </div>
              <div className="text-sm text-green-600">Unrestricted Users</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-800">
                {messengerPermissions.filter(p => p.is_restricted).length}
              </div>
              <div className="text-sm text-red-600">Restricted Users</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-800">
                {users.length - messengerPermissions.length}
              </div>
              <div className="text-sm text-blue-600">Default (No Restrictions)</div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-700">
              <p><strong>Messenger Permission System:</strong></p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><strong>Unrestricted users</strong> can message anyone in the system</li>
                <li><strong>Restricted users</strong> can only message approved contacts</li>
                <li><strong>Bidirectional permissions:</strong> Both users need permission to see each other</li>
                <li><strong>Default behavior:</strong> New users have no restrictions unless explicitly set</li>
                <li>Admin users can always message anyone regardless of restrictions</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MessengerPermissionsSettings;